import React, { useCallback, useEffect, useRef } from "react";
import { inlinePauseMarker } from "@veyso/core";
import { SyncPoint, type Filmstrip } from "./SyncPoint.js";

export interface ScriptSegment {
  id: string;
  /** Seconds into the recording where this line is spoken. */
  seconds: number;
  text: string;
  /** Silence held before this line, in seconds. Zero when there is none. */
  pauseBefore: number;
}

export interface ScriptDocumentProps {
  segments: ScriptSegment[];
  durationSeconds: number;
  filmstrip: Filmstrip | null;
  /** Fires as the user types; the caller keeps the draft. */
  onChange: (segmentId: string, text: string) => void;
  /** Sets the silence held before a line. Zero removes it. */
  onPauseChange: (segmentId: string, seconds: number) => void;
  onSeek: (seconds: number) => void;
  editedIds: ReadonlySet<string>;
  /** The line being spoken right now, or null when nothing is playing. */
  activeId: string | null;
  /** Scroll the active line into view — only while playing, never mid-edit. */
  follow: boolean;
}

/**
 * The script as one continuous document.
 *
 * A stack of separate boxes made the narration read as a list of unrelated
 * sentences, which is exactly the failure mode it encourages when writing:
 * every box invites its own complete thought, so the same fact gets stated
 * three times. Flowing it as prose with the sync points inline shows the
 * narration the way a viewer will hear it — one voice, in order — while still
 * saying precisely where each line lands in the video.
 *
 * Each segment is separately editable but visually continuous, so a save still
 * only re-voices the lines that changed.
 */
const ScriptDocumentBase: React.FC<ScriptDocumentProps> = ({
  segments,
  durationSeconds,
  filmstrip,
  onChange,
  onPauseChange,
  onSeek,
  editedIds,
  activeId,
  follow,
}) => {
  // The text each editable span was mounted with. Rendering the *original*
  // rather than the draft is what keeps the caret still: React compares against
  // an unchanging string, sees no difference, and never touches the DOM the
  // user is typing into.
  const mounted = useRef(new Map<string, string>());

  const initial = (segment: ScriptSegment) => {
    if (!mounted.current.has(segment.id)) mounted.current.set(segment.id, segment.text);
    return mounted.current.get(segment.id)!;
  };

  // Keeps the spoken line on screen as playback moves through the script.
  // Deliberately skipped when the caret is inside the document: scrolling the
  // text away from someone who is typing in it is worse than losing the follow.
  const activeRef = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!follow || !activeRef.current) return;
    if (activeRef.current.parentElement?.contains(document.activeElement)) return;
    activeRef.current.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [activeId, follow]);

  // Pasting into a contenteditable otherwise carries the source's markup in.
  const onPaste = useCallback((event: React.ClipboardEvent) => {
    event.preventDefault();
    const text = event.clipboardData.getData("text/plain").replace(/\s+/g, " ");
    event.currentTarget.ownerDocument.execCommand("insertText", false, text);
  }, []);

  /**
   * A beat inside a line, written at the caret.
   *
   * `insertText` rather than rebuilding the line: it is what the paste handler
   * already uses, it leaves the caret after what it wrote, and it keeps the
   * undo stack — a pause you can undo with the same keystroke as a typo is one
   * you will try.
   *
   * The marker stays visible as text on purpose. It moves with the sentence
   * when the words around it are edited, it survives a copy and paste, and it
   * can be corrected to `[0.8s]` by typing — none of which a decorated pill
   * inside a contenteditable would manage.
   */
  const insertPause = useCallback(
    (event: React.KeyboardEvent<HTMLSpanElement>) => {
      const line = event.currentTarget;
      line.ownerDocument.execCommand("insertText", false, ` ${inlinePauseMarker(1)} `);
      onChange(line.dataset.segmentId ?? "", line.textContent ?? "");
    },
    [onChange],
  );

  return (
    <div className="group/doc text-[13px] leading-[1.85] text-ink-100">
      {segments.map((segment, index) => (
        <React.Fragment key={segment.id}>
          <PauseRow seconds={segment.pauseBefore} onChange={(seconds) => onPauseChange(segment.id, seconds)} />
          <SyncPoint
            index={index + 1}
            seconds={segment.seconds}
            durationSeconds={durationSeconds}
            filmstrip={filmstrip}
            onClick={() => onSeek(segment.seconds)}
          />
          <span
            ref={segment.id === activeId ? activeRef : undefined}
            contentEditable
            suppressContentEditableWarning
            role="textbox"
            aria-label={`Narration ${index + 1}`}
            spellCheck
            onPaste={onPaste}
            data-segment-id={segment.id}
            // A beat inside the sentence, where the caret is. Between lines is
            // the pause row above; this is the one that is part of the delivery.
            onKeyDown={(event) => {
              if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== "k") return;
              event.preventDefault();
              insertPause(event);
            }}
            onInput={(event) => onChange(segment.id, event.currentTarget.textContent ?? "")}
            data-edited={editedIds.has(segment.id)}
            data-active={segment.id === activeId}
            // Composed here rather than as two data-variants of `bg-*`: those
            // have equal specificity, so which one won would depend on the order
            // Tailwind happened to emit them in.
            className={`rounded-[3px] outline-none ring-offset-2 ring-offset-bay-900 transition-colors focus:ring-1 focus:ring-scope-400/50 ${
              segment.id === activeId
                ? // A marker pen, not a tint. It is on screen for a couple of
                  // seconds at a time and has to be findable at a glance in a
                  // wall of text, so it takes the dark-on-yellow contrast a real
                  // highlighter has.
                  "bg-[var(--color-highlight)] text-[var(--color-on-accent)] shadow-[0_0_0_2px_var(--color-highlight)]"
                : editedIds.has(segment.id)
                  ? "bg-ember-500/10"
                  : ""
            }`}
          >
            {initial(segment)}
          </span>{" "}
        </React.Fragment>
      ))}
    </div>
  );
};

/**
 * Silence held before a line.
 *
 * Between two lines rather than inside one, because that is where the gesture
 * belongs: the script is already a document of lines with their timestamps down
 * the side. It also means no marker syntax parsed out of the speech, nothing for
 * the voice provider to interpret, and nothing to strip back out before the
 * words reach the captions.
 *
 * A pause needs no new audio at all — the words are unchanged, so every clip is
 * reused by hash and not one provider call is made. Silence is the only sound we
 * never have to buy.
 */
const PAUSE_STEPS = [0, 0.5, 1, 1.5, 2, 3, 5] as const;

const PauseRow: React.FC<{ seconds: number; onChange: (seconds: number) => void }> = ({ seconds, onChange }) => {
  const next = () => {
    const at = PAUSE_STEPS.indexOf(seconds as (typeof PAUSE_STEPS)[number]);
    return PAUSE_STEPS[(at < 0 ? 0 : at + 1) % PAUSE_STEPS.length];
  };

  if (seconds <= 0) {
    return (
      <button
        onClick={() => onChange(0.5)}
        title="Hold here before the next line"
        aria-label="Add a pause before this line"
        // Invisible until wanted: a document with an insert button between every
        // pair of lines reads as a form, not as a script.
        className="mx-1 rounded-full px-1.5 align-middle text-[10px] leading-none text-ink-400 opacity-0 transition-opacity hover:bg-bay-800 hover:text-ink-100 focus:opacity-100 group-hover/doc:opacity-60"
      >
        ⏸
      </button>
    );
  }

  return (
    <span className="mx-1 inline-flex items-center gap-1 align-middle">
      <button
        onClick={() => onChange(next())}
        title="Change how long the narration waits"
        aria-label={`Pause ${seconds} seconds — click to change`}
        className="rounded-full border border-dashed border-ember-500/55 bg-ember-500/10 px-2 py-[1px] text-[10.5px] font-semibold leading-normal text-ember-400 transition-colors hover:bg-ember-500/20"
      >
        ⏸ {seconds}s
      </button>
      <button
        onClick={() => onChange(0)}
        title="Remove this pause"
        aria-label="Remove this pause"
        className="rounded-full px-1 text-[11px] leading-none text-ink-400 transition-colors hover:text-ink-100"
      >
        ×
      </button>
    </span>
  );
};

/**
 * Memoised because the editor re-renders on every frame of playback, and this
 * subtree contains the contenteditable script. Re-running it sixty times a
 * second is both the most expensive thing on the page and the most fragile.
 */
export const ScriptDocument = React.memo(ScriptDocumentBase);
